import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import avatarFB from '@/assets/images/avatar-fb.png';
import avatarIG from '@/assets/images/avatar-ig.png';
import avatarLinkedIn from '@/assets/images/avatar-linkedin.png';
import avatarEmail from '@/assets/images/email-icon.png';
import mergedIcon from '@/assets/images/merged-icon.png';
import avatarSMS from '@/assets/images/sms-icon.png';
import useContactStore from '@/stores/ContactStore';
import useInboxStore from '@/stores/InboxStore';
import useUserStore from '@/stores/UserStore';
import { useHotkeys } from '@blueprintjs/core';
import { Loader2Icon } from 'lucide-react';
import { useInView } from 'react-intersection-observer';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useShallow } from 'zustand/react/shallow';

import {
  trackContactMessagesFocus,
  trackContatFBFetch,
  trackSendMessage,
} from '@/lib/analytics-event';
import { isMergedChannel } from '@/lib/utils';
import {
  fetchContactMessages,
  fetchContactMessagesFB,
  sendContactFileAttachment,
  sendContactMessage,
  sendEmailReply,
  sendPrivateReplyToComment,
  takeoverSalesBot,
} from '@/hooks/contact';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';

import CoPilotButton from '../CoPilotButton';
import EmailThreadList from './EmailThreadList';
import MergedMessageList from './MergedMessageList';
import MessageInput from './MessageInput';
import RegularMessageList from './RegularMessageList';

export default function ContactMessages() {
  const {
    page,
    setPage,
    messages,
    setNewMessage,
    setPrevPageMessages,
    load_fb_past_messages,
    setLoadFbPastMessages,
    channel,
    setContactOwner,
    addMessageToEmailThread,
  } = useContactStore(
    useShallow((state) => ({
      page: state.page,
      setPage: state.setPage,
      messages: state.messages,
      setNewMessage: state.setNewMessage,
      setPrevPageMessages: state.setPrevPageMessages,
      load_fb_past_messages: state.load_fb_past_messages,
      setLoadFbPastMessages: state.setLoadFbPastMessages,
      channel: state.channel,
      setContactOwner: state.setContactOwner,
      addMessageToEmailThread: state.addMessageToEmailThread,
    }))
  );
  const scrollBottomDivRef = useRef(null);
  const scrollAreaDivRef = useRef(null);
  const previousScrollHeight = useRef(0);
  const [fbFetchLoading, setFbFetchLoading] = useState(false);
  const [takeoverLoading, setTakeoverLoading] = useState(false);
  const [hoveredMessageId, setHoveredMessageId] = useState(null);
  const [messageLoading, setMessageLoading] = useState(false);
  const fetchingPageRef = useRef(null);

  const { owners, updateConversationNewMessage } = useInboxStore(
    useShallow((state) => ({
      owners: state.owners,
      updateConversationNewMessage: state.updateConversationNewMessage,
    }))
  );
  const { currentUser, copilotEnabled, igapiEnabled } = useUserStore(
    useShallow((state) => ({
      currentUser: state.user,
      copilotEnabled: state.copilotEnabled,
      igapiEnabled: state.igapiEnabled,
    }))
  );
  const {
    contactId,
    fullname,
    profile_picture_url,
    updateCommentResponded,
    latest_message_received_timestamp,
    latest_comment_received_timestamp,
    owner,
    setSelectedEmailThreadForReply,
  } = useContactStore(
    useShallow((state) => ({
      contactId: state._id,
      fullname: state.fullname,
      profile_picture_url: state.profile_picture_url,
      updateCommentResponded: state.updateCommentResponded,
      latest_message_received_timestamp:
        state.latest_message_received_timestamp,
      latest_comment_received_timestamp:
        state.latest_comment_received_timestamp,
      owner: state.owner,
      setSelectedEmailThreadForReply: state.setSelectedEmailThreadForReply,
    }))
  );

  const { ref, inView } = useInView();

  const { id } = useParams();
  const defaultPic = `https://files.sbccrm.com/sbccrm/sbccrm/contacts/pics/no-pic.jpg`;

  const handleNextResponse = useCallback(
    (messages, page, responseType = null) => {
      setPrevPageMessages(messages, responseType);
      setPage(page);
    },
    [setPrevPageMessages, setPage]
  );

  const handleFetchFromFbResponse = (messages, load_fb_past_messages) => {
    setPrevPageMessages(messages);
    setLoadFbPastMessages(load_fb_past_messages);
  };

  const getOwnerDetails = (ownerId, type, channel) => {
    if (ownerId === '') {
      if (type === 'image') {
        return channel === 'instagram'
          ? avatarIG
          : channel === 'facebook'
            ? avatarFB
            : channel === 'linkedin'
              ? avatarLinkedIn
              : channel === 'sms'
                ? avatarSMS
                : channel === 'email'
                  ? avatarEmail
                  : isMergedChannel(channel)
                    ? mergedIcon
                    : null;
      } else if (type === 'fullname' || type === 'initials') {
        return channel === 'instagram'
          ? 'IG'
          : channel === 'facebook'
            ? 'FB'
            : channel === 'linkedin'
              ? 'LI'
              : channel === 'sms'
                ? 'SMS'
                : channel === 'email'
                  ? 'EMAIL'
                  : isMergedChannel(channel)
                    ? 'MERGED'
                    : null;
      }
    }

    const currentOwner = owners.find((owner) => owner._id === ownerId);

    if (currentOwner) {
      if (type === 'initials') {
        return `${currentOwner.first_name.charAt(
          0
        )}${currentOwner.last_name.charAt(0)}`;
      } else if (type === 'image') {
        return currentOwner.profile_picture_url;
      } else if (type === 'fullname') {
        return `${currentOwner.first_name} ${currentOwner.last_name}`;
      }
    } else {
      return '';
    }
  };

  const fetchData = useCallback(
    async (contactId, type, page) => {
      // Inorder to keep syn between contactId and url, avoiding unnecessary fetch calls/page update
      if (contactId !== id) {
        // console.log('inside inview (check - failing)', contactId, id);

        return;
      }

      try {
        setMessageLoading(true);
        fetchingPageRef.current = page;
        const response = await fetchContactMessages(contactId, page);
        const { data, next_page_url, current_page, response_type } = response;

        // Use the same handler for both email threads and regular messages
        handleNextResponse(
          data,
          next_page_url ? current_page + 1 : null,
          response_type
        );
      } catch (error) {
        console.error('error fetching messages:', error);
      } finally {
        setMessageLoading(false);
        fetchingPageRef.current = null;
      }
    },
    [handleNextResponse, id]
  );

  const fetchFromIG = async () => {
    try {
      setFbFetchLoading(true);
      const response = await fetchContactMessagesFB(contactId, page);
      trackContatFBFetch();

      const { data, load_fb_past_messages } = response;
      handleFetchFromFbResponse(data, load_fb_past_messages);
    } catch (error) {
      console.error('error fetching messages:', error);
    }
    setFbFetchLoading(false);
  };

  const scrollToBottom = () => {
    const scrollableBottomDiv = scrollBottomDivRef.current;
    if (scrollableBottomDiv) {
      scrollableBottomDiv.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const calculateDaysDiff = (timestamp) => {
    const providedDate = new Date(timestamp);
    const currentDate = new Date();
    const diffTime = currentDate - providedDate;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const addMessage = async (message, type, options = null) => {
    var messageObj = null;
    var currentTimestamp = new Date().toISOString();

    switch (type) {
      case 'text':
        messageObj = {
          text: message,
          timestamp: currentTimestamp,
          direction: 'sent',
          user_id: currentUser._id,
          is_seen: null,
          is_deleted: null,
          reaction: null,
          emoji: null,
          message_type: 'text',
          media_url: null,
        };
        break;

      case 'image':
        messageObj = {
          timestamp: currentTimestamp,
          direction: 'sent',
          user_id: currentUser._id,
          is_seen: null,
          is_deleted: null,
          reaction: null,
          emoji: null,
          message_type: 'image',
          media_url: null,
        };
        break;

      case 'audio':
        messageObj = {
          timestamp: currentTimestamp,
          direction: 'sent',
          user_id: currentUser._id,
          is_seen: null,
          is_deleted: null,
          reaction: null,
          emoji: null,
          message_type: 'audio',
          media_url: null,
        };
        break;

      case 'video':
        messageObj = {
          timestamp: currentTimestamp,
          direction: 'sent',
          user_id: currentUser._id,
          is_seen: null,
          is_deleted: null,
          reaction: null,
          emoji: null,
          message_type: 'video',
          media_url: null,
        };
        break;

      case 'private_reply_comment':
        messageObj = {
          text: message,
          timestamp: currentTimestamp,
          direction: 'sent',
          user_id: currentUser._id,
          is_seen: null,
          is_deleted: null,
          reaction: null,
          emoji: null,
          message_type: 'text',
          media_url: null,
        };
        break;

      case 'reply_to_message':
        messageObj = {
          text: message,
          timestamp: currentTimestamp,
          direction: 'sent',
          user_id: currentUser._id,
          is_seen: null,
          is_deleted: null,
          reaction: null,
          emoji: null,
          message_type: 'text',
          media_url: null,
          is_reply: true,
          reply_to_message_id: options?.replyToMessageId,
          reply_to_text: options?.replyToMessage?.text || '',
          reply_to_message_type: options?.replyToMessage?.message_type,
          reply_to_media_url: options?.replyToMessage?.media_url || null,
        };
        break;

      case 'email_reply':
        messageObj = {
          content_text: message,
          content: message,
          timestamp: currentTimestamp,
          direction: 'sent',
          is_deleted: null,
          channel: 'email',
        };
        break;

      default:
        break;
    }

    try {
      let response;

      if (type === 'text') {
        console.log('currentUser', currentUser);
        if (
          latest_message_received_timestamp &&
          calculateDaysDiff(latest_message_received_timestamp) >= 7 &&
          (channel === 'instagram' || channel === 'facebook')
        ) {
          if (igapiEnabled) {
            response = await toast.promise(
              (async () => {
                response = await sendContactMessage(contactId, message);
                setNewMessage({ ...messageObj, _id: response.message_id });
                updateConversationNewMessage(
                  contactId,
                  message,
                  currentTimestamp
                );
                trackSendMessage(message.length, messageObj.message_type);
                return response;
              })(),
              {
                loading: 'Sending message...',
                success: 'Message sent successfully',
                error: (error) =>
                  error.response?.data?.message ||
                  'Failed to send the message (#900)',
              }
            );
          } else {
            toast.error(
              'You cannot send a message outside the 7 day window unless they respond back'
            );
            return;
          }
        } else {
          response = await sendContactMessage(contactId, message, channel);
          setNewMessage({ ...messageObj, _id: response.message_id });
          updateConversationNewMessage(contactId, message, currentTimestamp);
          trackSendMessage(message.length, messageObj.message_type);
        }
      } else if (type === 'image' || type === 'audio' || type === 'video') {
        if (
          latest_message_received_timestamp &&
          calculateDaysDiff(latest_message_received_timestamp) >= 7 &&
          (channel === 'instagram' || channel === 'facebook')
        ) {
          toast.error(
            'You cannot send a message outside the 7 day window unless they respond back'
          );
          return;
        }
        const formData = new FormData();
        formData.append('file', message);

        // Set audio_recording flag based on file extension
        if (type === 'audio') {
          const fileExtension = message.name.split('.').pop()?.toLowerCase();
          if (fileExtension === 'webm') {
            formData.append('audio_recording', 'true');
          }
        }

        // Get replyToMessageId from options if this is a reply
        const replyToMessageId = options?.replyToMessageId || null;
        response = await sendContactFileAttachment(
          contactId,
          formData,
          replyToMessageId
        );

        // Include reply data in messageObj if this is a reply
        const mediaMessageObj = replyToMessageId
          ? {
              ...messageObj,
              _id: response.message_id,
              media_url: response.media_url,
              is_reply: true,
              reply_to_message_id: replyToMessageId,
              reply_to_text: options?.replyToMessage?.text || '',
              reply_to_message_type: options?.replyToMessage?.message_type,
              reply_to_media_url: options?.replyToMessage?.media_url || null,
            }
          : {
              ...messageObj,
              _id: response.message_id,
              media_url: response.media_url,
            };

        setNewMessage(mediaMessageObj);
        const customMessage =
          type.charAt(0).toUpperCase() + type.slice(1) + ' message';
        updateConversationNewMessage(
          contactId,
          customMessage,
          currentTimestamp
        );

        // TODO: Write mixpanel event for file attachment
      } else if (type === 'private_reply_comment') {
        if (
          latest_comment_received_timestamp &&
          calculateDaysDiff(latest_comment_received_timestamp) >= 7
        ) {
          toast.error(
            'Comments which are older than 7 days cannot be private replied'
          );
          return;
        }
        const { sbcMessageId, commentText } = options;
        response = await sendPrivateReplyToComment(
          contactId,
          message,
          sbcMessageId
        );
        setNewMessage({
          ...messageObj,
          _id: response.message_id,
          private_reply_comment: true,
          private_reply_parent_data: {
            sbc_msg_id: sbcMessageId,
            text: commentText,
          },
        });
        updateConversationNewMessage(contactId, message, currentTimestamp);
        updateCommentResponded(sbcMessageId);
        // TODO: Write mixpanel event for private reply comment
      } else if (type === 'reply_to_message') {
        // Reply to message (Instagram)
        const { replyToMessageId } = options;
        response = await sendContactMessage(
          contactId,
          message,
          channel,
          replyToMessageId
        );
        setNewMessage({
          ...messageObj,
          _id: response.message_id,
        });
        updateConversationNewMessage(contactId, message, currentTimestamp);
        trackSendMessage(message.length, 'reply_to_message');
      } else if (type === 'email_reply') {
        const { threadId } = options;

        // Convert plain text to HTML for email
        const htmlContent = `<p>${message.replace(/\n/g, '<br>')}</p>`;

        response = await sendEmailReply(
          contactId,
          message,
          htmlContent,
          threadId
        );

        // Add the message to the email thread in the UI
        if (response && response.message_id) {
          const sendgridDetails =
            currentUser?.customer?.sendgrid_integration_details;

          const emailMessage = {
            _id: response.message_id,
            direction: 'sent',
            timestamp: currentTimestamp,
            channel: 'email',
            is_deleted: false,
            contact_id: contactId,
            sender_name: sendgridDetails?.from_name || '',
            sender_email: sendgridDetails?.from_email || '',
            user_id: currentUser._id,
            content: htmlContent,
            content_text: message,
            attachments: [],
            sent_status: 'sent',
          };

          addMessageToEmailThread(threadId, emailMessage);
          toast.success('Email reply sent successfully');
          updateConversationNewMessage(contactId, message, currentTimestamp);
        }
        // TODO: Write mixpanel event for email reply
      }
      return response;
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to send message');
    }
  };

  useEffect(() => {
    if (scrollAreaDivRef.current) {
      // Calculate the height difference of the scroll content
      const scrollHeightDifference =
        scrollAreaDivRef.current.scrollHeight - previousScrollHeight.current;

      // Set the scroll position to maintain the current visible position
      scrollAreaDivRef.current.scrollTop += scrollHeightDifference;

      // Save the new scroll height
      previousScrollHeight.current = scrollAreaDivRef.current.scrollHeight;
    }
  }, [messages]); // Dependency array

  // useEffect(() => {
  //   console.log('inside inview (page)', messageLoading, page, contactId);
  // }, [page]);

  useEffect(() => {
    if (contactId) {
      previousScrollHeight.current = 0;
      scrollToBottom();
    }
  }, [contactId]);

  useEffect(() => {
    // console.log('outside inview', messageLoading, page, contactId);
    if (
      inView &&
      !messageLoading &&
      page !== null &&
      page !== 0 &&
      fetchingPageRef.current !== page
    ) {
      // console.log('inside inview', messageLoading, page, contactId);
      fetchData(contactId, 'next', page);
    }
  }, [inView, messageLoading, page, contactId, fetchData]);

  const focusAndScroll = useCallback(() => {
    const element = scrollAreaDivRef.current;

    if (element) {
      element.focus();
      trackContactMessagesFocus('keyboard');

      // Add keydown event listener for arrow keys
      const handleArrowKeys = (event) => {
        switch (event.key) {
          case 'ArrowUp':
            element.scrollBy(0, -10);
            break;
          case 'ArrowDown':
            element.scrollBy(0, 10);
            break;
          default:
            break;
        }
      };

      element.addEventListener('keydown', handleArrowKeys);

      // Remove the event listener on blur
      element.onblur = () => {
        element.removeEventListener('keydown', handleArrowKeys);
      };
    }
  }, []);

  const hotkeys = useMemo(
    () => [
      {
        combo: 'alt+c',
        global: true,
        group: 'Contact Chat',
        label: 'Scroll Contact Messages',
        onKeyUp: focusAndScroll,
      },
    ],
    [focusAndScroll]
  );

  const handleEscapeKeyPress = useCallback((e) => {
    if (e.key === 'Escape') {
      scrollAreaDivRef?.current.blur();
    }
  }, []);

  const { handleKeyUp } = useHotkeys(hotkeys);

  const isAutopilotBot = useMemo(() => {
    const contactOwner = owners.find((o) => o._id === owner);
    return contactOwner?.role === 'autopilot_bot';
  }, [owners, owner]);

  const handleTakeover = () => {
    setTakeoverLoading(true);
    toast.promise(takeoverSalesBot(contactId), {
      loading: 'Taking over...',
      success: () => {
        setTakeoverLoading(false);
        setContactOwner(currentUser._id);
        return 'Takeover successful';
      },
      error: (error) => {
        setTakeoverLoading(false);
        return error.response?.data?.message || 'Failed to takeover';
      },
    });
  };

  const handleReplyToThread = useCallback(
    (threadId, subject, preview) => {
      setSelectedEmailThreadForReply(threadId, subject, preview);
    },
    [setSelectedEmailThreadForReply]
  );

  return (
    <>
      <div className='flex min-h-0 flex-1 flex-col overflow-hidden'>
        {contactId && (
          <ScrollArea
            ref={scrollAreaDivRef}
            className='flex-1'
            onKeyUp={handleKeyUp}
            showRing={true}
            onKeyDown={handleEscapeKeyPress}
            onMouseDown={() => trackContactMessagesFocus('mouse')}
          >
            {page !== null && page !== 0 && (
              <div ref={ref} className='flex items-center justify-center py-3'>
                {messageLoading && (
                  <Loader2Icon className='h-4 w-4 animate-spin' />
                )}
              </div>
            )}
            <>
              {isMergedChannel(channel) && messages.length > 0 ? (
                // Merged channel mixed content rendering (threads + regular messages)
                <MergedMessageList
                  messages={messages}
                  fullname={fullname}
                  profile_picture_url={profile_picture_url}
                  defaultPic={defaultPic}
                  getOwnerDetails={getOwnerDetails}
                  hoveredMessageId={hoveredMessageId}
                  setHoveredMessageId={setHoveredMessageId}
                  owners={owners}
                  currentUser={currentUser}
                  onReplyToThread={handleReplyToThread}
                  fbFetchLoading={fbFetchLoading}
                  fetchFromIG={fetchFromIG}
                  load_fb_past_messages={load_fb_past_messages}
                  page={page}
                />
              ) : messages.length > 0 && messages[0]?.thread_id ? (
                // Email thread-based rendering - detect threads by checking for thread_id
                <EmailThreadList
                  threads={messages}
                  fullname={fullname}
                  profile_picture_url={profile_picture_url}
                  defaultPic={defaultPic}
                  getOwnerDetails={getOwnerDetails}
                  setHoveredMessageId={setHoveredMessageId}
                  hoveredMessageId={hoveredMessageId}
                  owners={owners}
                  onReplyToThread={handleReplyToThread}
                />
              ) : messages.length > 0 ? (
                <RegularMessageList
                  messages={messages}
                  fullname={fullname}
                  profile_picture_url={profile_picture_url}
                  defaultPic={defaultPic}
                  channel={channel}
                  getOwnerDetails={getOwnerDetails}
                  hoveredMessageId={hoveredMessageId}
                  setHoveredMessageId={setHoveredMessageId}
                  owners={owners}
                  currentUser={currentUser}
                  fbFetchLoading={fbFetchLoading}
                  fetchFromIG={fetchFromIG}
                  load_fb_past_messages={load_fb_past_messages}
                  page={page}
                />
              ) : page === null ? (
                <div className='relative mx-10 my-5 flex items-center justify-center'>
                  <div className='absolute  left-0 right-0 -z-10 w-full border-t bg-primary' />
                  <span className='rounded-md bg-border px-4 py-2 text-xs'>
                    This is the start of conversation
                  </span>
                </div>
              ) : null}
            </>
            {messages.length > 0 ? (
              <div ref={scrollBottomDivRef} className='h-1' />
            ) : null}
          </ScrollArea>
        )}
      </div>
      {!isAutopilotBot ? (
        <MessageInput addMessage={addMessage} />
      ) : (
        <div className='flex items-center justify-between border-t bg-muted/50 px-4 py-3'>
          <div className='flex items-center gap-2'>
            <div className='h-2 w-2 animate-pulse rounded-full bg-primary' />
            <p className='text-sm text-muted-foreground'>
              This contact is currently handled by Revio Autopilot Bot
            </p>
          </div>
          <Button
            variant='outline'
            size='sm'
            onClick={handleTakeover}
            className='w-24'
            disabled={takeoverLoading}
          >
            {takeoverLoading ? (
              <Loader2Icon className='h-4 w-4 animate-spin' />
            ) : (
              'Takeover'
            )}
          </Button>
        </div>
      )}
      {copilotEnabled && (
        <CoPilotButton sidebarOpen={false} addMessage={addMessage} />
      )}
    </>
  );
}
